import { z } from 'zod';
import { addMonths, currentMonthSP, monthOf, parseYmd } from '../month';

const ymdSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD.')
  .refine((value) => {
    const { year, month, day } = parseYmd(value);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return parsed.getUTCFullYear() === year && parsed.getUTCMonth() + 1 === month && parsed.getUTCDate() === day;
  }, 'Data inválida.');
const ymSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

function ymdUtc(value: string): number {
  const { year, month, day } = parseYmd(value);
  return Date.UTC(year, month - 1, day);
}

export const expenseCalendarStatusSchema = z.enum(['all', 'pending', 'effected']);
export type ExpenseCalendarStatus = z.infer<typeof expenseCalendarStatusSchema>;

export const expenseCalendarRecurrenceSchema = z.enum([
  'all',
  'fixed',
  'installment',
  'repeat',
  'none',
]);
export type ExpenseCalendarRecurrence = z.infer<typeof expenseCalendarRecurrenceSchema>;

export const expenseCalendarTypeSchema = z.enum(['all', 'expense', 'income']);
export type ExpenseCalendarType = z.infer<typeof expenseCalendarTypeSchema>;

export const expenseCalendarQuerySchema = z
  .object({
    from: ymdSchema,
    to: ymdSchema,
    type: expenseCalendarTypeSchema.default('expense'),
    status: expenseCalendarStatusSchema.default('all'),
    recurrence: expenseCalendarRecurrenceSchema.default('all'),
    accountId: z.string().optional(),
    cardId: z.string().optional(),
    categoryIds: z.string().optional(),
    view: z.enum(['full', 'summary']).default('full'),
    limit: z.coerce.number().int().min(1).max(50).optional(),
    groupFixedCards: z.preprocess((value) => value === true || value === 'true', z.boolean()).default(false),
  })
  .refine((query) => query.from <= query.to, {
    message: 'A data inicial deve ser anterior ou igual à data final.',
    path: ['from'],
  })
  .refine((query) => {
    const start = ymdUtc(query.from);
    const end = ymdUtc(query.to);
    return end - start <= 366 * 24 * 60 * 60 * 1000;
  }, {
    message: 'O intervalo máximo do calendário é de 366 dias.',
    path: ['to'],
  })
  .refine((query) => monthOf(query.to) <= addMonths(currentMonthSP(), 24), {
    message: 'O calendário permite consultar até 24 meses no futuro.',
    path: ['to'],
  });
export type ExpenseCalendarQuery = z.infer<typeof expenseCalendarQuerySchema>;

const calendarEntitySchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const expenseCalendarTransactionItemSchema = z.object({
  kind: z.literal('transaction'),
  id: z.string(),
  type: z.enum(['income', 'expense']),
  date: ymdSchema,
  description: z.string(),
  amountCents: z.number().int(),
  effectuated: z.boolean(),
  recurrenceId: z.string().nullable(),
  recurrenceMode: z.enum(['fixed', 'installment', 'repeat']).nullable(),
  installmentIndex: z.number().int().nullable(),
  installmentTotal: z.number().int().nullable(),
  invoiceMonth: ymSchema.nullable(),
  category: calendarEntitySchema.extend({ color: z.string().nullable(), icon: z.string().nullable() }).nullable(),
  account: calendarEntitySchema.nullable(),
  card: calendarEntitySchema.nullable(),
});
export type ExpenseCalendarTransactionItem = z.infer<typeof expenseCalendarTransactionItemSchema>;

export const expenseCalendarFixedCardGroupSchema = z.object({
  kind: z.literal('fixed_card_group'),
  id: z.string(),
  type: z.literal('expense'),
  date: ymdSchema,
  dueDate: ymdSchema,
  description: z.string(),
  amountCents: z.number().int(),
  effectuated: z.boolean(),
  recurrenceId: z.null(),
  recurrenceMode: z.null(),
  installmentIndex: z.null(),
  installmentTotal: z.null(),
  invoiceMonth: ymSchema,
  category: z.null(),
  account: z.null(),
  card: calendarEntitySchema.extend({ color: z.string() }),
  pendingCents: z.number().int(),
  effectedCents: z.number().int(),
  groupedItems: z.array(expenseCalendarTransactionItemSchema),
});
export type ExpenseCalendarFixedCardGroup = z.infer<typeof expenseCalendarFixedCardGroupSchema>;

export const expenseCalendarItemSchema = z.discriminatedUnion('kind', [
  expenseCalendarTransactionItemSchema,
  expenseCalendarFixedCardGroupSchema,
]);
export type ExpenseCalendarItem = z.infer<typeof expenseCalendarItemSchema>;

export const expenseCalendarSummarySchema = z.object({
  totalExpenseCents: z.number().int(),
  incomeCents: z.number().int(),
  incomeCount: z.number().int(),
  pendingCents: z.number().int(),
  effectedCents: z.number().int(),
  fixedCents: z.number().int(),
  pendingCount: z.number().int(),
  effectedCount: z.number().int(),
  fixedCount: z.number().int(),
});
export type ExpenseCalendarSummary = z.infer<typeof expenseCalendarSummarySchema>;

export const expenseCalendarDaySchema = z.object({
  date: ymdSchema,
  totalCents: z.number().int(),
  incomeCents: z.number().int(),
  expenseCents: z.number().int(),
  pendingCents: z.number().int(),
  effectedCents: z.number().int(),
  fixedCents: z.number().int(),
  items: z.array(expenseCalendarItemSchema),
});
export type ExpenseCalendarDay = z.infer<typeof expenseCalendarDaySchema>;

export const expenseCalendarResponseSchema = z.object({
  from: ymdSchema,
  to: ymdSchema,
  summary: expenseCalendarSummarySchema,
  days: z.array(expenseCalendarDaySchema),
});
export type ExpenseCalendarResponse = z.infer<typeof expenseCalendarResponseSchema>;
